import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import {
  BdUiInlineNotificationComponent,
  BdUiProgressIndicatorComponent,
  BdUiStepProgressComponent,
} from '@bindoc-projects/ui-lib/feedback/ui';
import { ProgressBar } from 'primeng/progressbar';
import type {
  BdJobChainResponse,
  BdJobChainSummaryResponse,
} from './chain.model';
import {
  chainCounts,
  chainSteps,
  failedStepTitle,
  failedTask,
  taskErrorText,
} from './chain-progress';

/**
 * Progress of a job chain (BDA-6736) as `bd-ui-step-progress`: the steps
 * done, the running step with its own percentage and message, the steps
 * queued after it, and the cause once the chain failed. Display only — feed it
 * a `BdJobChainTracker` handle and react to its state yourself; success is the
 * caller's toast, never an inline message here. Rows keep their height from
 * running to done, so finishing does not shift the layout.
 *
 * - `chain = null` while the chain is looked up: one running step
 *   "Verarbeitung wird gestartet".
 * - `readingResult` after the chain finished while the caller still reads its
 *   result: a last running step "Ergebnisse werden geladen".
 * - A FAILED chain: the failed step stays red in the log (where) and a
 *   compact error note names it, "{Schritt} fehlgeschlagen", with the cause
 *   from `error.reason` (why). The heading is the caller's; `chainHeading`
 *   gives "Verarbeitung abgebrochen".
 * - `unavailable` when there is no chain status (job system down, no chain):
 *   an info note in place of the log; the caller shows the row's status.
 * - `mode="compact"` for a table row: one line with the split track of
 *   `bd-ui-progress-indicator` — members done of all, failures in red. No
 *   notes; an unavailable status renders nothing, the row shows its own.
 */
@Component({
  selector: 'bd-ui-chain-status',
  template: `
    @if (mode() === 'compact') {
      @if (!unavailable()) {
        @if (counts(); as counts) {
          <bd-ui-progress-indicator
            data-test="bd-chain-status-compact"
            mode="compact"
            [title]="title()"
            [state]="counts.state"
            [completedCount]="counts.completed"
            [totalCount]="counts.total"
            [errorCount]="counts.failed"
          />
        } @else {
          <p-progressbar
            data-test="bd-chain-status-compact"
            mode="indeterminate"
            [style]="{ height: '6px' }"
            [attr.aria-label]="title()"
          />
        }
      }
    } @else if (unavailable()) {
      <bd-ui-inline-notification
        data-test="bd-chain-status-unavailable"
        severity="info"
        title="Fortschritt nicht verfügbar"
        [message]="unavailableMessage()"
      />
    } @else {
      <bd-ui-step-progress
        data-test="bd-chain-status"
        [heading]="title()"
        [steps]="steps()"
        [totalSteps]="steps().length"
      />
      @if (error(); as error) {
        <bd-ui-inline-notification
          class="mt-4 block"
          data-test="bd-chain-status-error"
          severity="error"
          variant="compact"
          [title]="errorTitle()"
          [message]="error"
        />
      }
    }
  `,
  imports: [
    BdUiStepProgressComponent,
    BdUiProgressIndicatorComponent,
    BdUiInlineNotificationComponent,
    ProgressBar,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdUiChainStatusComponent {
  public readonly chain = input<
    BdJobChainSummaryResponse | BdJobChainResponse | null
  >(null);

  /** Heading of the step log; also the hint until the first step reports. */
  public readonly title = input.required<string>();

  public readonly readingResult = input(false);

  /** Labels per task type, over the defaults. */
  public readonly labels = input<Readonly<Record<string, string>>>();

  public readonly unavailable = input(false);

  public readonly unavailableMessage = input(
    'Die Verarbeitung läuft im Hintergrund weiter.',
  );

  /** Title of the error note; defaults to the failed step, "Analysen vorberechnen fehlgeschlagen". */
  public readonly failedTitle = input<string>();

  /** `full`: step log; `compact`: one row for tables. */
  public readonly mode = input<'full' | 'compact'>('full');

  /** Compact counts; `null` while the chain is looked up. */
  protected readonly counts = computed(() => {
    const chain = this.chain();
    return chain ? chainCounts(chain) : null;
  });

  protected readonly steps = computed(() =>
    chainSteps(this.chain(), {
      readingResult: this.readingResult(),
      labels: this.labels(),
    }),
  );

  protected readonly errorTitle = computed(
    () => this.failedTitle() ?? failedStepTitle(this.chain(), this.labels()),
  );

  /** `null` unless the chain FAILED. */
  protected readonly error = computed(() => {
    const chain = this.chain();
    if (chain?.status !== 'FAILED') {
      return null;
    }
    return taskErrorText(failedTask(chain)?.error);
  });
}
